import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUser, FaLock, FaEye, FaEyeSlash, FaBookOpen, FaShieldAlt, FaChartBar, FaUsers } from 'react-icons/fa';
import { useApp } from '../../../context/AppContext';
import { jwtDecode } from 'jwt-decode';
import { authAPI } from '../../../utils/api';
import { showWelcomeToast } from '../../../utils/authToasts';
import stbbLogo from '../../../uploads/stbb_logo.png';

const LOCKOUT_TIME = 10;

const FEATURES = [
  { icon: FaBookOpen, text: 'Textbook procurement & distribution tracking' },
  { icon: FaUsers, text: 'Multi-role user management system' },
  { icon: FaChartBar, text: 'Real-time enrollment & inventory analytics' },
  { icon: FaShieldAlt, text: 'Secure, role-based access control' },
];

function LoginPage({ redirectPath = '/dashboard' }) {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState('');
  const [loading, setLoading] = useState(false);
  const [lockout, setLockout] = useState(false);
  const [lockoutTimer, setLockoutTimer] = useState(LOCKOUT_TIME);
  const lockoutInterval = useRef(null);
  const { login, isAuthenticated } = useApp();
  const { roles = [] } = useApp();

  useEffect(() => {
    if (!isAuthenticated) return;
    let target = '/dashboard';
    if (roles.includes('ROLE_PUBLISHER')) target = '/publisher/dashboard';
    else if (roles.includes('ROLE_QC') || roles.includes('ROLE_QUALITY_CONTROL')) target = '/qc/dashboard';
    else if (roles.includes('ROLE_STORE_OFFICER') || roles.includes('ROLE_STORE')) target = '/so/dashboard';
    else if (roles.includes('ROLE_GATE_OFFICER')) target = '/go/dashboard';
    navigate(target, { replace: true });
  }, [isAuthenticated, roles, navigate]);

  useEffect(() => {
    if (lockout) {
      lockoutInterval.current = setInterval(() => {
        setLockoutTimer(prev => {
          if (prev <= 1) {
            clearInterval(lockoutInterval.current);
            setLockout(false);
            return LOCKOUT_TIME;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(lockoutInterval.current);
  }, [lockout]);

  const validate = () => {
    const errs = {};
    if (!username.trim()) errs.username = 'Username is required';
    if (!password) errs.password = 'Password is required';
    else if (password.length < 6) errs.password = 'Password must be at least 6 characters';
    return errs;
  };

  const handleLogin = async e => {
    e.preventDefault();
    if (lockout) return;
    setServerError('');
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setLoading(true);
    try {
      const response = await authAPI.login({ username, password });
      const { token } = response.data;
      if (!token) throw new Error('No token received from server');
      localStorage.setItem('jwt_token', token);
      await login();

      let target = '/dashboard';
      try {
        const dec = jwtDecode(token);
        const r = dec.roles || [];
        if (r.includes('ROLE_PUBLISHER')) target = '/publisher/dashboard';
        else if (r.includes('ROLE_QC') || r.includes('ROLE_QUALITY_CONTROL')) target = '/qc/dashboard';
        else if (r.includes('ROLE_STORE_OFFICER') || r.includes('ROLE_STORE')) target = '/so/dashboard';
        else if (r.includes('ROLE_GATE_OFFICER')) target = '/go/dashboard';
      } catch (e) { /* ignore decode errors */ }

      // Show modern welcome toast, then navigate
      await showWelcomeToast(username);
      navigate(target);
    } catch (error) {
      let message = 'Invalid credentials. Please try again.';
      if (error.response?.status === 403) {
        message = 'Your account is locked or disabled. Contact support.';
      } else if (error.code === 'ECONNABORTED' || error.message === 'Network Error') {
        message = 'Network error. Please check your connection.';
      } else if (error.response?.status >= 500) {
        message = 'Server error. Please try again later.';
      }
      setServerError(message);
    } finally {
      setLoading(false);
    }
  };

  const handlePaste = e => e.preventDefault();
  const handleCopy = e => e.preventDefault();

  const handleUsernameChange = e => {
    setUsername(e.target.value);
    if (errors.username) setErrors(p => ({ ...p, username: '' }));
    if (serverError) setServerError('');
  };

  const handlePasswordChange = e => {
    setPassword(e.target.value);
    if (errors.password) setErrors(p => ({ ...p, password: '' }));
    if (serverError) setServerError('');
  };

  return (
    <div style={styles.page}>
      {/* Left Panel — Branding */}
      <div style={styles.leftPanel}>
        <div style={styles.leftInner}>
          <div style={styles.logoRow}>
            <img src={stbbLogo} alt="STBB" style={styles.logoImg} />
            <div>
              <div style={styles.logoTitle}>STBB Portal</div>
              <div style={styles.logoSub}>Sindh Textbook Board</div>
            </div>
          </div>

          <div style={styles.brandHeadline}>
            Empowering Education<br />
            <span style={styles.brandAccent}>Through Technology</span>
          </div>
          <p style={styles.brandDesc}>
            A unified management system for procurement, distribution, enrollment, and inventory operations.
          </p>

          <div style={styles.featureList}>
            {FEATURES.map(({ icon: Icon, text }, i) => (
              <div key={i} style={styles.featureItem}>
                <div style={styles.featureIcon}><Icon /></div>
                <span style={styles.featureText}>{text}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={styles.leftFooter}>
          © {new Date().getFullYear()} Sindh Textbook Board. All rights reserved.
        </div>
      </div>

      {/* Right Panel — Form */}
      <div style={styles.rightPanel}>
        <div style={styles.formCard}>
          <div style={styles.formHeader}>
            <h2 style={styles.formTitle}>Welcome back</h2>
            <p style={styles.formSub}>Sign in to your account to continue</p>
          </div>

          <div style={styles.dividerStripe} />

          {serverError && (
            <div style={styles.serverError} role="alert">
              <FaShieldAlt style={{ marginRight: 8, flexShrink: 0 }} />
              <span>{serverError}</span>
            </div>
          )}

          {lockout && (
            <div style={styles.lockoutBar}>
              Too many failed attempts. Please wait{' '}
              <strong style={{ color: '#e74c3c' }}>{lockoutTimer}s</strong> before trying again.
              <div style={styles.lockoutProgress}>
                <div style={{ ...styles.lockoutFill, width: `${(lockoutTimer / LOCKOUT_TIME) * 100}%` }} />
              </div>
            </div>
          )}

          <form onSubmit={handleLogin} noValidate>
            {/* Username */}
            <div style={styles.fieldGroup}>
              <label style={styles.fieldLabel} htmlFor="username">Username</label>
              <div style={{ ...styles.inputWrap, ...(errors.username ? styles.inputWrapError : {}) }}>
                <FaUser style={styles.inputIcon} />
                <input
                  id="username"
                  type="text"
                  value={username}
                  onChange={handleUsernameChange}
                  placeholder="Enter your username"
                  style={styles.input}
                  disabled={loading || lockout}
                  autoComplete="username"
                  autoFocus
                />
              </div>
              {errors.username && <span style={styles.fieldError}>{errors.username}</span>}
            </div>

            {/* Password */}
            <div style={styles.fieldGroup}>
              <label style={styles.fieldLabel} htmlFor="password">Password</label>
              <div style={{ ...styles.inputWrap, ...(errors.password ? styles.inputWrapError : {}) }}>
                <FaLock style={styles.inputIcon} />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={handlePasswordChange}
                  placeholder="••••••••"
                  style={{ ...styles.input, paddingRight: 44 }}
                  disabled={loading || lockout}
                  autoComplete="current-password"
                  onPaste={handlePaste}
                  onCopy={handleCopy}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  style={styles.eyeBtn}
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
              {errors.password && <span style={styles.fieldError}>{errors.password}</span>}
            </div>

            <button
              type="submit"
              style={{ ...styles.submitBtn, ...(loading || lockout ? styles.submitBtnDisabled : {}) }}
              disabled={loading || lockout}
            >
              {loading ? (
                <span style={styles.btnLoader}>
                  <span style={styles.spinnerDot} />
                  Signing in...
                </span>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          <div style={styles.publisherLink}>
            <a href="/publisher/login" style={styles.publisherAnchor}>Sign in as Publisher instead</a>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .login-form-card { animation: fadeIn 0.4s ease; }
      `}</style>
    </div>
  );
}

const styles = {
  page: {
    display: 'flex',
    minHeight: '100vh',
    fontFamily: "'Poppins', sans-serif",
  },
  // Left Panel
  leftPanel: {
    flex: '0 0 45%',
    background: 'linear-gradient(160deg, #16a085 0%, #0f6fad 100%)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: '48px 56px',
    color: '#fff',
    position: 'relative',
    overflow: 'hidden',
  },
  leftInner: { flex: 1 },
  logoRow: { display: 'flex', alignItems: 'center', gap: 14, marginBottom: 56 },
  logoImg: { width: 48, height: 48, borderRadius: 10, background: 'rgba(255,255,255,0.15)', padding: 4 },
  logoTitle: { fontSize: '1.2rem', fontWeight: 700, letterSpacing: '-0.01em' },
  logoSub: { fontSize: '0.75rem', opacity: 0.75, marginTop: 1 },
  brandHeadline: {
    fontSize: '2.4rem',
    fontWeight: 700,
    lineHeight: 1.25,
    marginBottom: 20,
    letterSpacing: '-0.02em',
  },
  brandAccent: { color: 'rgba(255,255,255,0.75)' },
  brandDesc: {
    fontSize: '1rem',
    lineHeight: 1.7,
    opacity: 0.8,
    marginBottom: 48,
    maxWidth: 380,
  },
  featureList: { display: 'flex', flexDirection: 'column', gap: 16 },
  featureItem: { display: 'flex', alignItems: 'center', gap: 14 },
  featureIcon: {
    width: 38,
    height: 38,
    borderRadius: 10,
    background: 'rgba(255,255,255,0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    fontSize: '0.9rem',
  },
  featureText: { fontSize: '0.9rem', opacity: 0.9 },
  leftFooter: { fontSize: '0.75rem', opacity: 0.55, marginTop: 48 },

  // Right Panel
  rightPanel: {
    flex: 1,
    background: '#f7f8fa',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px 32px',
  },
  formCard: {
    background: '#fff',
    borderRadius: 20,
    padding: '44px 40px 36px',
    width: '100%',
    maxWidth: 420,
    boxShadow: '0 8px 40px rgba(0,0,0,0.08)',
    animation: 'fadeIn 0.4s ease',
  },
  formHeader: { marginBottom: 24 },
  formTitle: { fontSize: '1.75rem', fontWeight: 700, color: '#1a202c', margin: '0 0 6px 0' },
  formSub: { color: '#718096', fontSize: '0.95rem', margin: 0 },
  dividerStripe: {
    height: 4,
    borderRadius: 2,
    background: 'linear-gradient(90deg, #16a085, #10ac84, #0f6fad)',
    marginBottom: 28,
  },

  // Alerts
  serverError: {
    background: 'rgba(231, 76, 60, 0.08)',
    border: '1px solid rgba(231, 76, 60, 0.2)',
    borderRadius: 10,
    padding: '12px 16px',
    color: '#c0392b',
    fontSize: '0.875rem',
    marginBottom: 20,
    display: 'flex',
    alignItems: 'flex-start',
    gap: 4,
  },
  lockoutBar: {
    background: 'rgba(243, 156, 18, 0.08)',
    border: '1px solid rgba(243, 156, 18, 0.25)',
    borderRadius: 10,
    padding: '12px 16px',
    color: '#7d4e00',
    fontSize: '0.875rem',
    marginBottom: 20,
  },
  lockoutProgress: {
    height: 4,
    background: '#f0e0c0',
    borderRadius: 2,
    marginTop: 10,
    overflow: 'hidden',
  },
  lockoutFill: {
    height: '100%',
    background: '#e67e22',
    borderRadius: 2,
    transition: 'width 1s linear',
  },

  // Fields
  fieldGroup: { marginBottom: 20 },
  fieldLabel: {
    display: 'block',
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#374151',
    marginBottom: 8,
  },
  inputWrap: {
    display: 'flex',
    alignItems: 'center',
    border: '1.5px solid #e2e8f0',
    borderRadius: 10,
    background: '#f8fafc',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    position: 'relative',
  },
  inputWrapError: {
    borderColor: '#e74c3c',
    background: 'rgba(231, 76, 60, 0.03)',
  },
  inputIcon: {
    color: '#a0aec0',
    marginLeft: 14,
    marginRight: 2,
    flexShrink: 0,
    fontSize: '0.9rem',
  },
  input: {
    flex: 1,
    border: 'none',
    background: 'transparent',
    padding: '13px 14px',
    fontSize: '0.95rem',
    color: '#2d3748',
    outline: 'none',
    fontFamily: "'Poppins', sans-serif",
    width: '100%',
  },
  eyeBtn: {
    position: 'absolute',
    right: 14,
    background: 'none',
    border: 'none',
    padding: 0,
    color: '#a0aec0',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    fontSize: '0.95rem',
  },
  fieldError: {
    display: 'block',
    fontSize: '0.78rem',
    color: '#e74c3c',
    marginTop: 5,
  },

  // Submit
  submitBtn: {
    width: '100%',
    padding: '14px',
    background: 'linear-gradient(135deg, #10ac84, #0f6fad)',
    color: '#fff',
    border: 'none',
    borderRadius: 10,
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'opacity 0.2s, transform 0.1s',
    fontFamily: "'Poppins', sans-serif",
    marginTop: 8,
    letterSpacing: '0.01em',
    boxShadow: '0 4px 15px rgba(16, 172, 132, 0.3)',
  },
  submitBtnDisabled: {
    opacity: 0.65,
    cursor: 'not-allowed',
    transform: 'none',
  },
  btnLoader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
  },
  spinnerDot: {
    width: 16,
    height: 16,
    border: '2px solid rgba(255,255,255,0.3)',
    borderTopColor: '#fff',
    borderRadius: '50%',
    display: 'inline-block',
    animation: 'spin 0.7s linear infinite',
  },
  publisherLink: {
    textAlign: 'center',
    marginTop: 24,
    paddingTop: 20,
    borderTop: '1px solid #f0f0f0',
  },
  publisherAnchor: {
    color: '#0f6fad',
    fontSize: '0.875rem',
    textDecoration: 'none',
    fontWeight: 500,
  },
};

export default LoginPage;
